import { GROCERY_API_PATHS, RESTAURANT_API_PATHS } from '../constants/apiPaths';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { getInventoryOwnerContext } from './inventoryEntity';

export { getInventoryOwnerContext } from './inventoryEntity';
export { buildInventoryOwnerPayload, getInventoryOwnerRequiredMessage } from './inventoryEntity';

function authHeaders() {
  const token = localStorage.getItem('Token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Normalize one row from GET /groceries → groceryProducts[]. */
export function normalizeGroceryProduct(product) {
  if (!product || typeof product !== 'object') return null;
  const id = product.id ?? product.Id ?? product.groceryProductId;
  if (id == null) return null;
  return {
    id,
    groceryProductId: id,
    productCode: product.productCode ?? product.ProductCode ?? '',
    productName: product.productName ?? product.Product_Name ?? product.itemName ?? '',
    groceryCategoryId:
      product.groceryCategoryId ?? product.Grocery_Category_Id ?? '',
    groceryCategoryName:
      product.groceryCategoryName ?? product.Grocery_Category_Name ?? '',
    unitId: product.unitId ?? product.Unit_Id ?? '',
    unitName: product.unitName ?? product.Unit_Name ?? '',
    unitSymbol: product.unitSymbol ?? product.Unit_Symbol ?? '',
  };
}

/**
 * GET /groceries — catalog of grocery products (for add-inventory picker).
 * Response: { groceryProducts: [...] }
 */
export async function fetchGroceryProducts() {
  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.groceries, {
    headers: authHeaders(),
  });
  const raw =
    response.data?.groceryProducts ??
    response.data?.GroceryProducts ??
    (Array.isArray(response.data) ? response.data : []);
  return (Array.isArray(raw) ? raw : [])
    .map(normalizeGroceryProduct)
    .filter(Boolean);
}

/**
 * GET /restaurants/:restaurantId/inventory-items — { items: [ { item: {...}, level: {...} }, ... ] }.
 * GET /restaurant/inventory-item/:inventoryItemId — { item: {...}, level: {...} }.
 */
export function getInventorySellingPrice(row) {
  if (!row || typeof row !== 'object') return null;
  const price = row.sellingPrice ?? row.Selling_Price ?? row.unitPrice ?? row.Unit_Price;
  return price != null && price !== '' ? price : null;
}

export function getInventoryBarcode(row) {
  if (!row || typeof row !== 'object') return '';
  return String(row.barcode ?? row.Barcode ?? '').trim();
}

export function getInventoryStatus(row) {
  if (!row || typeof row !== 'object') return '';
  return String(row.status ?? row.Status ?? '').trim();
}

function formatPackQtyText(qtyNum, packQty) {
  if (!Number.isFinite(qtyNum)) return String(packQty).trim();
  if (qtyNum % 1 === 0) return String(Math.trunc(qtyNum));
  return String(Number(qtyNum.toFixed(6)));
}

/**
 * Display pack size with compact units: liter→L, 1000ml→1 L, 1000gm→1 Kg.
 * @param {unknown} packQty
 * @param {unknown} unitRaw
 * @param {{ spaced?: boolean }} [options]
 */
export function formatPackQuantityLabel(packQty, unitRaw, { spaced = false } = {}) {
  const unit = String(unitRaw ?? '').trim();
  const unitKey = unit.toLowerCase().replace(/[\s._-]+/g, '');
  const hasQty = packQty != null && packQty !== '';
  const qtyNum = hasQty ? Number(packQty) : NaN;

  const isLiter = /^(l|ltr|lt|liter|litre|liters|litres)$/.test(unitKey);
  const isMl = /^(ml|mls|milliliter|millilitre|milliliters|millilitres)$/.test(unitKey);
  const isKg = /^(kg|kgs|kilogram|kilograms)$/.test(unitKey);
  const isGm = /^(g|gm|gms|gr|gram|grams)$/.test(unitKey);

  let displayQty = hasQty ? qtyNum : null;
  let displayUnit = unit;

  if (isLiter) {
    displayUnit = 'L';
  } else if (isKg) {
    displayUnit = 'Kg';
  } else if (isMl && Number.isFinite(qtyNum) && qtyNum > 0 && qtyNum % 1000 === 0) {
    displayQty = qtyNum / 1000;
    displayUnit = 'L';
  } else if (isGm && Number.isFinite(qtyNum) && qtyNum > 0 && qtyNum % 1000 === 0) {
    displayQty = qtyNum / 1000;
    displayUnit = 'Kg';
  } else if (isMl) {
    displayUnit = 'ml';
  } else if (isGm) {
    displayUnit = 'gm';
  }

  if (!hasQty) return displayUnit;
  const qtyText = formatPackQtyText(displayQty, packQty);
  if (!displayUnit) return qtyText;
  return spaced ? `${qtyText} ${displayUnit}` : `${qtyText}${displayUnit}`;
}

export function getInventoryPackLabel(row) {
  if (!row || typeof row !== 'object') return '';
  const packQty = row.packQuantity ?? row.Pack_Quantity;
  const unit = row.unitSymbol ?? row.Unit_Symbol ?? row.unitName ?? row.Unit_Name ?? '';
  return formatPackQuantityLabel(packQty, unit, { spaced: false });
}

export function normalizeInventoryApiEntry(entry) {
  if (entry == null || typeof entry !== 'object') return null;

  const hasNestedItem = entry.item != null && typeof entry.item === 'object';
  if (hasNestedItem) {
    const i = entry.item;
    const l = entry.level != null && typeof entry.level === 'object' ? entry.level : {};
    const inventoryItemId = i.inventoryItemId ?? i.Inventory_Item_Id ?? i.inventory_item_id;
    if (inventoryItemId == null) return null;

    return {
      inventoryItemId,
      itemName: i.itemName ?? i.Item_Name ?? l.itemName ?? '',
      description: i.description ?? i.Description ?? '',
      imagePath: i.imagePath ?? i.Image_Path ?? '',
      imageUrl: i.imageUrl ?? i.Image_Url ?? '',
      mfgDate: i.mfgDate ?? i.Mfg_Date ?? l.mfgDate ?? l.Mfg_Date ?? '',
      expDate: i.expDate ?? i.Exp_Date ?? l.expDate ?? l.Exp_Date ?? '',
      groceryCategoryId: i.groceryCategoryId ?? i.Grocery_Category_Id ?? '',
      groceryCategoryName: i.groceryCategoryName ?? i.Grocery_Category_Name ?? '',
      brandId: i.brandId ?? i.Brand_Id ?? '',
      brandName: i.brandName ?? i.Brand_Name ?? '',
      groceryId: i.groceryId ?? i.Grocery_Id ?? i.groceryProductId ?? null,
      productCode: i.productCode ?? i.ProductCode ?? '',
      unitId: i.unitId ?? i.Unit_Id ?? '',
      unitName: i.unitName ?? i.Unit_Name ?? '',
      unitSymbol: i.unitSymbol ?? i.Unit_Symbol ?? '',
      unitPrice: i.unitPrice ?? i.Unit_Price ?? '',
      sellingPrice: i.sellingPrice ?? i.Selling_Price ?? null,
      mrp: i.mrp ?? i.MRP ?? null,
      barcode: i.barcode ?? i.Barcode ?? '',
      status: i.status ?? i.Status ?? '',
      packQuantity: i.packQuantity ?? i.Pack_Quantity ?? null,
      storeId: i.storeId ?? i.Store_Id ?? l.storeId ?? null,
      restaurantId: i.restaurantId ?? i.Restaurant_Id ?? l.restaurantId,
      quantityInStock: l.quantityInStock ?? l.Quantity_In_Stock ?? '',
      reorderLevel: l.reorderLevel ?? l.Reorder_Level ?? '',
      reorderQuantity: l.reorderQuantity ?? l.Reorder_Quantity ?? '',
      inventoryId: l.inventoryId ?? l.Inventory_Id ?? null,
      comments: l.comments ?? l.Comments ?? '',
      createdById: i.createdById ?? i.Created_By_Id ?? l.createdById ?? l.Created_By_Id ?? null,
      createdDate: i.createdDate ?? i.Created_Date ?? l.createdDate ?? l.Created_Date ?? '',
      updatedById: i.updatedById ?? i.Updated_By_Id ?? l.updatedById ?? l.Updated_By_Id ?? null,
      updatedDate: i.updatedDate ?? i.Updated_Date ?? l.updatedDate ?? l.Updated_Date ?? '',
    };
  }

  const id =
    entry.inventoryItemId ??
    entry.storeInventoryItemId ??
    entry.groceryItemId ??
    entry.Inventory_Item_Id ??
    entry.inventory_item_id;
  if (id == null) return null;
  return {
    ...entry,
    inventoryItemId: id,
    storeInventoryItemId: entry.storeInventoryItemId ?? id,
    groceryItemId: entry.groceryItemId ?? id,
    storeId: entry.storeId ?? entry.Store_Id ?? null,
    imagePath: entry.imagePath ?? entry.Image_Path ?? '',
    imageUrl: entry.imageUrl ?? entry.Image_Url ?? '',
    barcode: entry.barcode ?? entry.Barcode ?? '',
    sellingPrice: entry.sellingPrice ?? entry.Selling_Price ?? null,
    mrp: entry.mrp ?? entry.MRP ?? null,
    status: entry.status ?? entry.Status ?? '',
    packQuantity: entry.packQuantity ?? entry.Pack_Quantity ?? null,
    brandId: entry.brandId ?? entry.Brand_Id ?? null,
    brandName: entry.brandName ?? entry.Brand_Name ?? '',
    mfgDate: entry.mfgDate ?? entry.Mfg_Date ?? '',
    expDate: entry.expDate ?? entry.Exp_Date ?? '',
  };
}

export function normalizeInventoryList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeInventoryApiEntry).filter(Boolean);
}

/** Image URL from inventory row (prefers API imageUrl, else builds from imagePath). */
export function getInventoryImageUrl(row) {
  const directUrl = row?.imageUrl ?? row?.Image_Url ?? '';
  if (directUrl && String(directUrl).trim()) {
    return String(directUrl).trim().replace(/%22|"$/g, '');
  }
  const path = row?.imagePath ?? row?.Image_Path ?? '';
  if (!path || !String(path).trim()) return '';
  const cleanPath = String(path).trim().replace(/^\/+/, '').replace(/%22|"$/g, '');
  const base =
    axiosRestaurantInstance.defaults.baseURL ||
    process.env.REACT_APP_API_COMPANY_URL ||
    '';
  if (!base) return `/uploads/${cleanPath}`;
  try {
    return `${new URL(base).origin}/uploads/${cleanPath}`;
  } catch {
    return `${base.replace(/\/$/, '')}/uploads/${cleanPath}`;
  }
}

/** Attachment download/view URL (prefers API fileUrl, else builds from filePath). */
export function getPurchaseAttachmentUrl(attachment) {
  if (!attachment || typeof attachment !== 'object') return '';
  const directUrl = attachment.fileUrl ?? attachment.File_Url ?? '';
  if (directUrl && String(directUrl).trim()) {
    return String(directUrl).trim().replace(/%22|"$/g, '');
  }
  const path = attachment.filePath ?? attachment.File_Path ?? '';
  if (!path || !String(path).trim()) return '';
  const cleanPath = String(path).trim().replace(/^\/+/, '').replace(/%22|"$/g, '');
  const base =
    axiosRestaurantInstance.defaults.baseURL ||
    process.env.REACT_APP_API_COMPANY_URL ||
    '';
  if (!base) return `/uploads/${cleanPath}`;
  try {
    return `${new URL(base).origin}/uploads/${cleanPath}`;
  } catch {
    return `${base.replace(/\/$/, '')}/uploads/${cleanPath}`;
  }
}

/** GET /restaurants/:restaurantId/inventory-items */
export async function fetchInventoryItemsByRestaurant(restaurantId) {
  const rid = Number(restaurantId);
  if (!rid || Number.isNaN(rid)) return [];
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantInventoryItemsByRestaurantId(rid),
    { headers: authHeaders() }
  );
  const raw = response.data?.items ?? response.data?.Items ?? [];
  return normalizeInventoryList(Array.isArray(raw) ? raw : []);
}

/** GET /groceries/store/:storeId/inventory-items */
export async function fetchInventoryItemsByStore(storeId) {
  const sid = Number(storeId);
  if (!sid || Number.isNaN(sid)) return [];
  const response = await axiosRestaurantInstance.get(
    GROCERY_API_PATHS.storeInventoryItemsByStoreId(sid),
    { headers: authHeaders() }
  );
  const raw = response.data?.items ?? response.data?.Items ?? [];
  return normalizeInventoryList(Array.isArray(raw) ? raw : []);
}

export async function fetchInventoryItemsByOwner(owner = getInventoryOwnerContext()) {
  if (!owner?.ownerId) return [];
  if (owner.storeId > 0) {
    return fetchInventoryItemsByStore(owner.storeId);
  }
  return fetchInventoryItemsByRestaurant(owner.restaurantId);
}

/**
 * PUT /inventory/item/:inventoryItemId
 */
export async function updateInventoryItem(inventoryItemId, body, owner = getInventoryOwnerContext()) {
  const token = localStorage.getItem('Token');
  const imageFile = body?.imageFile ?? body?.image ?? body?.Image ?? null;
  const hasImage = imageFile != null && !owner.isStore;
  let payload = body;
  let headers = {
    ...(token && { Authorization: `Bearer ${token}` }),
  };

  if (hasImage) {
    const level = body?.level ?? {};
    const formData = new FormData();
    formData.append('restaurantId', String(body?.restaurantId ?? body?.Restaurant_Id ?? ''));
    formData.append('itemName', String(body?.itemName ?? body?.Item_Name ?? ''));
    formData.append('unitId', String(body?.unitId ?? body?.Unit_Id ?? ''));
    formData.append('unitPrice', String(body?.unitPrice ?? body?.Unit_Price ?? ''));
    formData.append('updatedById', String(body?.updatedById ?? body?.Updated_By_Id ?? ''));
    formData.append(
      'reorderLevel',
      String(body?.reorderLevel ?? level?.reorderLevel ?? level?.Reorder_Level ?? '')
    );
    formData.append(
      'reorderQuantity',
      String(body?.reorderQuantity ?? level?.reorderQuantity ?? level?.Reorder_Quantity ?? '')
    );
    formData.append('description', String(body?.description ?? body?.Description ?? ''));
    formData.append(
      'groceryCategoryId',
      String(body?.groceryCategoryId ?? body?.Grocery_Category_Id ?? '')
    );
    formData.append('comments', String(body?.comments ?? level?.comments ?? level?.Comments ?? ''));
    formData.append(
      'quantityInStock',
      String(body?.quantityInStock ?? level?.quantityInStock ?? level?.Quantity_In_Stock ?? '')
    );
    if (level.mfgDate ?? body?.mfgDate) {
      formData.append('mfgDate', String(level.mfgDate ?? body?.mfgDate ?? ''));
    }
    if (level.expDate ?? body?.expDate) {
      formData.append('expDate', String(level.expDate ?? body?.expDate ?? ''));
    }
    formData.append('image', imageFile, imageFile?.name ?? 'inventory-image');
    payload = formData;
    headers = {
      ...headers,
      'Content-Type': 'multipart/form-data',
    };
  } else if (owner.isStore) {
    const level = body?.level ?? {};
    payload = {
      storeId: body?.storeId ?? owner.storeId,
      itemName: body?.itemName ?? body?.Item_Name,
      description: body?.description ?? body?.Description,
      unitPrice: body?.unitPrice ?? body?.Unit_Price,
      updatedById: body?.updatedById ?? body?.Updated_By_Id,
      level: {
        quantityInStock: level.quantityInStock ?? body?.quantityInStock,
        reorderLevel: level.reorderLevel ?? body?.reorderLevel,
        reorderQuantity: level.reorderQuantity ?? body?.reorderQuantity,
        comments: level.comments ?? body?.comments,
        mfgDate: level.mfgDate ?? body?.mfgDate,
        expDate: level.expDate ?? body?.expDate,
      },
    };
    headers = {
      ...headers,
      'Content-Type': 'application/json',
    };
  }

  const path =
    owner.isStore
      ? GROCERY_API_PATHS.storeInventoryItemById(inventoryItemId)
      : RESTAURANT_API_PATHS.inventoryItemLegacy(inventoryItemId);
  const response = await axiosRestaurantInstance.put(path, payload, {
    headers,
  });
  return response.data;
}

/** Build POST /restaurant/inventory/item JSON body (item + initial stock level). */
export function buildCreateInventoryItemPayload(body) {
  const level = body?.level ?? {};
  const createdById = Number(
    body?.createdById ?? body?.Created_By_Id ?? level?.levelCreatedById ?? 0
  );
  const levelCreatedById = Number(level?.levelCreatedById ?? createdById);

  return {
    restaurantId: Number(body?.restaurantId ?? body?.Restaurant_Id),
    groceryId: Number(
      body?.groceryId ??
        body?.groceryProductId ??
        body?.Grocery_Id ??
        body?.Grocery_Product_Id
    ),
    itemName: String(body?.itemName ?? body?.Item_Name ?? '').trim(),
    description: String(body?.description ?? body?.Description ?? '').trim(),
    unitId: Number(body?.unitId ?? body?.Unit_Id),
    groceryCategoryId: Number(body?.groceryCategoryId ?? body?.Grocery_Category_Id),
    unitPrice: String(body?.unitPrice ?? body?.Unit_Price ?? '').trim(),
    mfgDate: String(body?.mfgDate ?? level?.mfgDate ?? '').trim() || undefined,
    expDate: String(body?.expDate ?? level?.expDate ?? '').trim() || undefined,
    createdById: createdById,
    level: {
      quantityInStock: Number(
        level.quantityInStock ?? body?.quantityInStock ?? body?.Quantity_In_Stock
      ),
      reorderLevel: Number(level.reorderLevel ?? body?.reorderLevel ?? body?.Reorder_Level),
      reorderQuantity: Number(
        level.reorderQuantity ?? body?.reorderQuantity ?? body?.Reorder_Quantity
      ),
      comments: String(level.comments ?? body?.comments ?? body?.Comments ?? '').trim(),
      mfgDate: String(level.mfgDate ?? body?.mfgDate ?? '').trim() || undefined,
      expDate: String(level.expDate ?? body?.expDate ?? '').trim() || undefined,
      levelCreatedById: levelCreatedById > 0 ? levelCreatedById : createdById,
    },
  };
}

/**
 * POST /restaurant/inventory/item — creates item and stock level.
 * Response: { item, level, message }
 */
export async function createInventoryItem(body) {
  const payload = buildCreateInventoryItemPayload(body);
  const imageFile = body?.imageFile ?? body?.image ?? body?.Image ?? null;
  const hasImage = imageFile != null;

  if (hasImage) {
    const formData = new FormData();
    formData.append('restaurantId', String(payload.restaurantId));
    formData.append('groceryId', String(payload.groceryId));
    formData.append('itemName', payload.itemName);
    formData.append('description', payload.description);
    formData.append('unitId', String(payload.unitId));
    formData.append('groceryCategoryId', String(payload.groceryCategoryId));
    formData.append('unitPrice', payload.unitPrice);
    formData.append('createdById', String(payload.createdById));
    formData.append('level', JSON.stringify(payload.level));
    formData.append('image', imageFile, imageFile?.name ?? 'inventory-image');

    const response = await axiosRestaurantInstance.post(
      RESTAURANT_API_PATHS.restaurantInventoryItem,
      formData,
      {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data ?? {};
  }

  const response = await axiosRestaurantInstance.post(
    RESTAURANT_API_PATHS.restaurantInventoryItem,
    payload,
    {
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
    },
  });
  return response.data ?? {};
}

/**
 * GET /restaurant/:restaurantId/suppliers — list suppliers for restaurant.
 */
export function normalizeSupplier(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    supplierId: row.supplierId ?? row.Supplier_Id,
    restaurantId: row.restaurantId ?? row.Restaurant_Id ?? null,
    storeId: row.storeId ?? row.Store_Id ?? null,
    supplierName: row.supplierName ?? row.Supplier_Name ?? '',
    contactPerson: row.contactPerson ?? row.Contact_Person ?? '',
    mobile: row.mobile ?? row.Mobile ?? '',
    altNo: row.altNo ?? row.Alt_No ?? '',
    email: row.email ?? row.Email ?? '',
    address: row.address ?? row.Address ?? '',
    gstNumber: row.gstNumber ?? row.GST_Number ?? '',
    isActive: row.isActive ?? row.Is_Active,
  };
}

export async function fetchSuppliersByRestaurant(restaurantId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantSuppliersByRestaurantId(restaurantId),
    {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const d = response.data;
  const raw =
    d?.suppliers ??
    d?.Suppliers ??
    d?.items ??
    d?.Items ??
    (Array.isArray(d) ? d : []);
  return (Array.isArray(raw) ? raw : []).map(normalizeSupplier);
}

export async function fetchSuppliersByStore(storeId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.get(
    GROCERY_API_PATHS.storeSuppliersByStoreId(storeId),
    {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    }
  );
  const d = response.data;
  const raw =
    d?.suppliers ??
    d?.Suppliers ??
    d?.items ??
    d?.Items ??
    (Array.isArray(d) ? d : []);
  return (Array.isArray(raw) ? raw : []).map(normalizeSupplier);
}

export async function fetchSuppliersByOwner(owner = getInventoryOwnerContext()) {
  if (!owner?.ownerId) return [];
  if (owner.storeId > 0) {
    return fetchSuppliersByStore(owner.storeId);
  }
  return fetchSuppliersByRestaurant(owner.restaurantId);
}

/**
 * GET /restaurant/suppliers/:supplierId — single supplier (flat object in response body).
 */
export async function fetchSupplierDetail(supplierId, owner = getInventoryOwnerContext()) {
  const token = localStorage.getItem('Token');
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeSupplierById(supplierId)
      : RESTAURANT_API_PATHS.restaurantSupplierById(supplierId);
  const response = await axiosRestaurantInstance.get(path, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const body = response.data;
  if (body && typeof body === 'object' && body.supplierId == null && (body.supplier != null || body.Supplier != null)) {
    return normalizeSupplier(body.supplier ?? body.Supplier);
  }
  return normalizeSupplier(body);
}

/**
 * GET /restaurants/:restaurantId/users — list users for restaurant (created/updated by lookups).
 */
export async function fetchRestaurantUsers(restaurantId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantUsersByRestaurantId(restaurantId),
    {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const raw = response.data?.users ?? response.data?.Users ?? [];
  return Array.isArray(raw) ? raw : [];
}

/**
 * GET /restaurants/users/:userId — single user for display name fallback.
 */
export async function fetchRestaurantUserById(userId) {
  const token = localStorage.getItem('Token');
  const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantUserById(userId), {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  const body = response.data;
  if (body?.user != null) return body.user;
  if (body?.User != null) return body.User;
  return body;
}

/**
 * POST /restaurants/suppliers
 * Body: restaurantId, supplierName, contactPerson, email, altNo, mobile, address, isActive, createdById.
 * Response: { message, supplier }.
 */
export async function createSupplier(body, owner = getInventoryOwnerContext()) {
  const token = localStorage.getItem('Token');
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeSuppliers
      : RESTAURANT_API_PATHS.restaurantsSuppliers;
  const response = await axiosRestaurantInstance.post(path, body, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return response.data;
}

/**
 * PUT /restaurants/suppliers/:supplierId
 * Body: restaurantId, supplierName, contactPerson, email, altNo, mobile, address, isActive, updatedById.
 * Response: { message, supplier }.
 */
export async function updateSupplier(supplierId, body, owner = getInventoryOwnerContext()) {
  const token = localStorage.getItem('Token');
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeSupplierById(supplierId)
      : RESTAURANT_API_PATHS.restaurantSupplierById(supplierId);
  const response = await axiosRestaurantInstance.put(path, body, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return response.data;
}

/**
 * DELETE /restaurants/suppliers/:supplierId — soft delete; response typically { message }.
 */
export async function deleteSupplier(supplierId, owner = getInventoryOwnerContext()) {
  const token = localStorage.getItem('Token');
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeSupplierById(supplierId)
      : RESTAURANT_API_PATHS.restaurantSupplierById(supplierId);
  const response = await axiosRestaurantInstance.delete(path, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return response.data;
}

export function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isGroceryOrderPurchase(row) {
  const id = row?.groceryOrderId ?? row?.Grocery_Order_Id;
  return id != null && String(id).trim() !== '';
}

export function purchaseSupplierDisplay(row) {
  if (isGroceryOrderPurchase(row)) return '—';
  const name = row?.supplierName ?? row?.Supplier_Name;
  return name != null && String(name).trim() !== '' ? String(name).trim() : '—';
}

export function purchaseStoreDisplay(row) {
  if (!isGroceryOrderPurchase(row)) return '—';
  const name = row?.storeName ?? row?.Store_Name;
  return name != null && String(name).trim() !== '' ? String(name).trim() : '—';
}

/** Payment types for inventory purchase (restaurant_purchase_t.Payment_Type). */
export const PURCHASE_PAYMENT_TYPE_OPTIONS = [
  { value: 'Cash', label: 'Cash' },
  { value: 'UPI', label: 'UPI' },
  { value: 'Card', label: 'Card' },
  { value: 'GPay', label: 'GPay' },
  { value: 'Paytm', label: 'Paytm' },
  { value: 'PhonePe', label: 'PhonePe' },
  { value: 'Bank Transfer', label: 'Bank Transfer' },
  { value: 'Cheque', label: 'Cheque' },
  { value: 'Credit', label: 'Credit Card' },
];

/** Normalize purchase header from list or create response. */
export function normalizePurchase(row) {
  if (!row || typeof row !== 'object') return row;
  const items = Array.isArray(row.items) ? row.items.map(normalizePurchaseLineItem) : row.items;
  return {
    ...row,
    purchaseId: row.purchaseId ?? row.Purchase_Id ?? row.purchase_id,
    Purchase_Id: row.purchaseId ?? row.Purchase_Id ?? row.purchase_id,
    restaurantId: row.restaurantId ?? row.Restaurant_Id ?? null,
    storeId: row.storeId ?? row.Store_Id ?? null,
    restaurantName: row.restaurantName ?? row.Restaurant_Name ?? '',
    storeName: row.storeName ?? row.Store_Name ?? '',
    supplierName: row.supplierName ?? row.Supplier_Name ?? '',
    supplierId: row.supplierId ?? row.Supplier_Id ?? null,
    groceryOrderId: row.groceryOrderId ?? row.Grocery_Order_Id ?? null,
    billNumber: row.billNumber ?? row.Bill_Number ?? '',
    paymentType: row.paymentType ?? row.Payment_Type ?? '',
    purchaseDate: row.purchaseDate ?? row.Purchase_Date ?? row.purchase_date ?? '',
    purchaseStatus: row.purchaseStatus ?? row.Purchase_Status ?? '',
    expDeliveryDate: row.expDeliveryDate ?? row.Exp_Delivery_Date ?? row.exp_delivery_date ?? '',
    totalAmount: row.totalAmount ?? row.Total_Amount ?? row.total ?? row.Total,
    createdById: row.createdById ?? row.Created_By_Id,
    createdByName: row.createdByName ?? row.Created_By_Name ?? '',
    createdDate: row.createdDate ?? row.Created_Date ?? '',
    updatedById: row.updatedById ?? row.Updated_By_Id,
    updatedByName: row.updatedByName ?? row.Updated_By_Name ?? '',
    updatedDate: row.updatedDate ?? row.Updated_Date ?? '',
    items,
  };
}

export function normalizePurchaseLineItem(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    purchaseItemId: row.purchaseItemId ?? row.Purchase_Item_Id,
    purchaseId: row.purchaseId ?? row.Purchase_Id,
    productId: row.productId ?? row.Product_Id ?? row.groceryProductId,
    productName: row.productName ?? row.Product_Name ?? '',
    productCode: row.productCode ?? row.Product_Code ?? '',
    quantity: row.quantity ?? row.Quantity,
    pricePerUnit: row.pricePerUnit ?? row.Price_Per_Unit ?? row.price ?? row.Price,
    discountPercent: row.discountPercent ?? row.Discount_Percent ?? null,
    discountAmount: row.discountAmount ?? row.Discount_Amount ?? null,
    totalPrice: row.totalPrice ?? row.Total_Price ?? null,
    purchaseAmount: row.purchaseAmount ?? row.Purchase_Amount ?? null,
    groceryCategoryName: row.groceryCategoryName ?? row.Grocery_Category_Name ?? '',
    unitName: row.unitName ?? row.Unit_Name ?? '',
    unitSymbol: row.unitSymbol ?? row.Unit_Symbol ?? '',
  };
}

export async function fetchInventoryPurchasesByOwner(owner = getInventoryOwnerContext()) {
  if (!owner?.ownerId) return [];
  if (owner.storeId > 0) {
    return fetchInventoryPurchasesByStore(owner.storeId);
  }
  return fetchInventoryPurchasesByRestaurant(owner.restaurantId);
}

/**
 * GET /restaurant/:restaurantId/inventory-purchases
 * Response: { purchases: [...] }.
 */
export async function fetchInventoryPurchasesByRestaurant(restaurantId) {
  const response = await axiosRestaurantInstance.get(
    RESTAURANT_API_PATHS.restaurantInventoryPurchasesByRestaurantId(restaurantId),
    { headers: authHeaders() }
  );
  const d = response.data;
  const raw =
    d?.purchases ??
    d?.Purchases ??
    (Array.isArray(d) ? d : []);
  return (Array.isArray(raw) ? raw : []).map(normalizePurchase);
}

export async function fetchInventoryPurchasesByStore(storeId) {
  const response = await axiosRestaurantInstance.get(
    GROCERY_API_PATHS.storeInventoryPurchasesByStoreId(storeId),
    { headers: authHeaders() }
  );
  const d = response.data;
  const raw =
    d?.purchases ??
    d?.Purchases ??
    (Array.isArray(d) ? d : []);
  return (Array.isArray(raw) ? raw : []).map(normalizePurchase);
}

/**
 * GET /restaurants/inventory-purchases/:purchaseId
 * Response: purchase object with items[].
 */
export async function fetchInventoryPurchaseById(purchaseId, owner = getInventoryOwnerContext()) {
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeInventoryPurchaseById(purchaseId)
      : RESTAURANT_API_PATHS.restaurantInventoryPurchaseById(purchaseId);
  const response = await axiosRestaurantInstance.get(path, { headers: authHeaders() });
  const body = response.data;
  if (body && typeof body === 'object' && body.purchaseId == null && (body.purchase != null || body.Purchase != null)) {
    return normalizePurchase(body.purchase ?? body.Purchase);
  }
  return normalizePurchase(body);
}

/**
 * PUT /restaurants/inventory-purchases/:purchaseId
 * Body: { purchaseStatus, expDeliveryDate, updatedById }.
 * Response: { message, purchase }.
 */
export async function updateInventoryPurchase(purchaseId, body, owner = getInventoryOwnerContext()) {
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeInventoryPurchaseById(purchaseId)
      : RESTAURANT_API_PATHS.restaurantInventoryPurchaseById(purchaseId);
  const response = await axiosRestaurantInstance.put(path, body, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const data = response.data ?? {};
  if (data.purchase) {
    return { ...data, purchase: normalizePurchase(data.purchase) };
  }
  return data;
}

/**
 * POST /restaurants/inventory-purchases
 * Body: { restaurantId, supplierId, purchaseDate, billNumber?, paymentType?, createdById, items: [...] }
 * Response: { message, purchase }.
 */
export async function createInventoryPurchase(body, owner = getInventoryOwnerContext()) {
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeInventoryPurchases
      : RESTAURANT_API_PATHS.restaurantsInventoryPurchases;
  const response = await axiosRestaurantInstance.post(path, body, {
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  const data = response.data ?? {};
  if (data.purchase) {
    return { ...data, purchase: normalizePurchase(data.purchase) };
  }
  return data;
}

/** @deprecated Use fetchInventoryPurchasesByRestaurant */
export async function fetchPurchasesByRestaurant(restaurantId) {
  return fetchInventoryPurchasesByRestaurant(restaurantId);
}

/** @deprecated Use createInventoryPurchase */
export async function createPurchase(body) {
  return createInventoryPurchase(body);
}

/** Normalize one row from purchase attachments list or upload response. */
export function normalizePurchaseAttachment(row) {
  if (!row || typeof row !== 'object') return null;
  const attachmentId = row.attachmentId ?? row.Attachment_Id ?? row.attachment_id;
  if (attachmentId == null) return null;
  return {
    attachmentId,
    purchaseId: row.purchaseId ?? row.Purchase_Id ?? row.purchase_id,
    fileName: row.fileName ?? row.File_Name ?? '',
    filePath: row.filePath ?? row.File_Path ?? '',
    fileUrl: row.fileUrl ?? row.File_Url ?? '',
    uploadedAt: row.uploadedAt ?? row.Uploaded_At ?? row.uploaded_at ?? '',
    uploadedById: row.uploadedById ?? row.Uploaded_By_Id ?? row.uploaded_by_id,
  };
}

/**
 * GET purchase attachments (restaurant or store).
 * Response: { purchaseId, attachments: [...] }.
 */
export async function fetchPurchaseAttachments(purchaseId, owner = getInventoryOwnerContext()) {
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeInventoryPurchaseAttachments(purchaseId)
      : RESTAURANT_API_PATHS.restaurantInventoryPurchaseAttachments(purchaseId);
  const response = await axiosRestaurantInstance.get(path, { headers: authHeaders() });
  const d = response.data ?? {};
  const raw = d.attachments ?? d.Attachments ?? [];
  return {
    purchaseId: d.purchaseId ?? d.Purchase_Id ?? purchaseId,
    attachments: (Array.isArray(raw) ? raw : [])
      .map(normalizePurchaseAttachment)
      .filter(Boolean),
  };
}

/**
 * POST /restaurants/inventory-purchases/:purchaseId/attachments
 * multipart: fileName, image (file), uploadedById.
 * Response: { message, attachment }.
 */
export async function uploadPurchaseAttachment(
  purchaseId,
  { fileName, image, uploadedById },
  owner = getInventoryOwnerContext()
) {
  const formData = new FormData();
  formData.append('fileName', String(fileName ?? '').trim() || 'bill');
  formData.append('image', image, image?.name ?? 'attachment');
  formData.append('uploadedById', String(uploadedById ?? ''));
  const path =
    owner.storeId > 0
      ? GROCERY_API_PATHS.storeInventoryPurchaseAttachments(purchaseId)
      : RESTAURANT_API_PATHS.restaurantInventoryPurchaseAttachments(purchaseId);
  const response = await axiosRestaurantInstance.post(path, formData, {
      headers: {
        ...authHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  const data = response.data ?? {};
  if (data.attachment) {
    return {
      ...data,
      attachment: normalizePurchaseAttachment(data.attachment),
    };
  }
  return data;
}

/** DELETE /inventory/item/:inventoryItemId — removes item and level; response { message }. */
export async function deleteInventoryItem(inventoryItemId, owner = getInventoryOwnerContext()) {
  const token = localStorage.getItem('Token');
  const path =
    owner.isStore
      ? GROCERY_API_PATHS.storeInventoryItemById(inventoryItemId)
      : RESTAURANT_API_PATHS.inventoryItemLegacy(inventoryItemId);
  const response = await axiosRestaurantInstance.delete(path, {
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  return response.data;
}

/**
 * GET /restaurant/inventory-item/:inventoryItemId
 * Response: { item: {...}, level: {...} }
 */
export async function fetchInventoryItemDetail(inventoryItemId, owner = getInventoryOwnerContext()) {
  const id = Number(inventoryItemId);
  if (!id || Number.isNaN(id)) return null;
  const path =
    owner.isStore
      ? GROCERY_API_PATHS.storeInventoryItemById(id)
      : RESTAURANT_API_PATHS.restaurantInventoryItemById(id);
  const response = await axiosRestaurantInstance.get(path, { headers: authHeaders() });
  const body = response.data ?? {};
  if (body.item != null || body.level != null) {
    return normalizeInventoryApiEntry(body);
  }
  const nested = body.data ?? body.Data;
  if (nested && typeof nested === 'object') {
    return normalizeInventoryApiEntry(nested);
  }
  return normalizeInventoryApiEntry(body);
}
